import React, { useState } from 'react'

import {
    Theme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    TableSortLabel,
    Container,
    Popover,
    MenuItem,
    Chip,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import TimelineIcon from '@material-ui/icons/Timeline'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import ApartmentIcon from '@material-ui/icons/Apartment'
import PublicIcon from '@material-ui/icons/Public'
import { makeStyles } from '@material-ui/core/styles'
import { Pagination } from '@material-ui/lab'

import { formatPhoneForDisplay } from '../../../helpers'
import { User } from '../../../models'
import { AvatarCircle } from '../../../components'
import { UserActivity } from '../../../hooks/useOrganizationManagement'
import { ModelMap } from '../../../models'

type Order = 'asc' | 'desc'
interface HeadCell {
    id: keyof User | 'activity' | 'count' | 'initial_training_progress'
    label: string
    sortable: boolean
}

const headCells: HeadCell[] = [
    { id: 'profile_picture', label: '', sortable: false },
    { id: 'name', label: 'Name', sortable: true },
    { id: 'email', label: 'Email', sortable: true },
    { id: 'phone', label: 'Phone', sortable: true },
    { id: 'cell_phone', label: 'Cell Phone', sortable: true },
    { id: 'activity', label: 'Last Activity', sortable: true },
    { id: 'count', label: '30-Day Activity', sortable: true },
    { id: 'companies', label: 'Properties', sortable: true },
    {
        id: 'initial_training_progress',
        label: 'Training Progress',
        sortable: true,
    },
]

const useStyles = makeStyles((theme: Theme) => ({
    iconButton: {
        border: '1px solid',
        borderRadius: '50%',
        padding: theme.spacing(0.5),
        marginRight: theme.spacing(1),
    },
    tableCell: {
        flex: 1,
    },
    headerCell: {
        flex: 1,
    },
    tableHead: {
        backgroundColor: theme.palette.background.paper,
    },
    stickyHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 250px)',
        overflowY: 'auto',
        width: '100%',
    },
    paginationContainer: {
        borderTop: `1px solid ${theme.palette.grey[400]}`,
        padding: theme.spacing(1),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        width: '100%',
        maxWidth: 'none',
        backgroundColor: theme.palette.background.paper,
    },
    rootContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: 0,
        maxWidth: 'none',
    },
    infoButton: {
        padding: 4,
        marginLeft: theme.spacing(1),
    },
    activityCell: {
        display: 'flex',
        alignItems: 'center',
    },
    actionButtons: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: 160,
        gap: theme.spacing(1),
    },
    roleChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(0.5),
    },
    regionalManagerChip: {
        fontWeight: 700,
    },
    propertyManagerChip: {
        border: '1px solid #1e88e5',
        color: '#1e88e5',
        fontWeight: 700,
    },
    popoverPaper: {
        minWidth: 280,
        borderRadius: 10,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    menuItem: {
        fontWeight: 500,
    },
    menuItemIcon: {
        minWidth: 30,
        color: theme.palette.text.secondary,
    },
    propertySelectHeader: {
        opacity: 0.75,
        fontSize: '0.85rem',
        fontWeight: 600,
        pointerEvents: 'none',
    },
}))

interface Props {
    users: User[]
    userListActivityMap: ModelMap<UserActivity>
    onClickRemoveUserFromWorkspaces: (user: User) => void
    onClickAddUser: (user: User) => void
    onClickUserActivity: (user: User) => void
    onMarkAsRegionalManager: (user: User) => void
    onMarkAsPropertyManager: (user: User, companyId: number) => void
}

export const OrganizationUserTable = (props: Props) => {
    const {
        users,
        userListActivityMap,
        onClickRemoveUserFromWorkspaces,
        onClickAddUser,
        onClickUserActivity,
        onMarkAsRegionalManager,
        onMarkAsPropertyManager,
    } = props

    const classes = useStyles()
    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState<
        keyof User | 'activity' | 'count' | 'initial_training_progress'
    >('name')
    const [page, setPage] = useState(1)
    const itemsPerPage = 25
    const [anchorEl, setAnchorEl] = useState<{
        [key: string]: HTMLElement | null
    }>({})
    const [
        propertyManagerSelectionUserId,
        setPropertyManagerSelectionUserId,
    ] = useState<string | null>(null)

    const handleRequestSort = (
        property:
            | keyof User
            | 'activity'
            | 'count'
            | 'initial_training_progress',
    ) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const sortedUsers = React.useMemo(() => {
        const comparator = (a: User, b: User) => {
            if (orderBy === 'activity') {
                const aTime = userListActivityMap[a.id]?.latest_timestamp || ''
                const bTime = userListActivityMap[b.id]?.latest_timestamp || ''
                return order === 'desc'
                    ? bTime.localeCompare(aTime)
                    : aTime.localeCompare(bTime)
            }
            if (orderBy === 'count') {
                const aCount = userListActivityMap[a.id]?.log_count || 0
                const bCount = userListActivityMap[b.id]?.log_count || 0
                return order === 'desc' ? bCount - aCount : aCount - bCount
            }
            if (orderBy === 'initial_training_progress') {
                const getWatchedCount = (user: User) => {
                    const progress = user.initial_training_progress
                    if (!progress) return 0
                    return [
                        progress.video_1_watched,
                        progress.video_2_watched,
                        progress.video_3_watched,
                        progress.video_4_watched,
                    ].filter(Boolean).length
                }
                const aCount = getWatchedCount(a)
                const bCount = getWatchedCount(b)
                return order === 'desc' ? bCount - aCount : aCount - bCount
            }
            if (orderBy === 'companies') {
                const aCount = a.companies?.length ?? 0
                const bCount = b.companies?.length ?? 0
                return order === 'desc' ? bCount - aCount : aCount - bCount
            }

            if (!a[orderBy] && !b[orderBy]) return 0
            if (!a[orderBy]) return 1
            if (!b[orderBy]) return -1

            return order === 'desc'
                ? String(b[orderBy]).localeCompare(String(a[orderBy]))
                : String(a[orderBy]).localeCompare(String(b[orderBy]))
        }

        return [...users].sort(comparator)
    }, [users, order, orderBy, userListActivityMap])

    const start = (page - 1) * itemsPerPage
    const end = Math.min(start + itemsPerPage, sortedUsers.length)
    const numPages = Math.ceil(sortedUsers.length / itemsPerPage)
    const pageData = sortedUsers.slice(start, end)

    const handleMoreVertClick = (
        event: React.MouseEvent<HTMLElement>,
        userId: string,
    ) => {
        setAnchorEl({ ...anchorEl, [userId]: event.currentTarget })
        setPropertyManagerSelectionUserId(null)
    }

    const handlePopoverClose = (userId: string) => {
        setAnchorEl({ ...anchorEl, [userId]: null })
        setPropertyManagerSelectionUserId(null)
    }

    const handleMarkAsRegionalManager = (user: User) => {
        onMarkAsRegionalManager(user)
        handlePopoverClose(String(user.id))
    }

    const handleMarkAsPropertyManager = (user: User, companyId: number) => {
        onMarkAsPropertyManager(user, companyId)
        handlePopoverClose(String(user.id))
    }

    const handlePropertyManagerMenuItemClick = (user: User) => {
        const companies = user.companies ?? []
        if (companies.length === 1) {
            handleMarkAsPropertyManager(user, companies[0].id)
            return
        }

        if (companies.length > 1) {
            setPropertyManagerSelectionUserId(String(user.id))
            return
        }

        handlePopoverClose(String(user.id))
    }

    return (
        <Container className={classes.rootContainer}>
            <TableContainer className={classes.tableContainer}>
                <Table stickyHeader style={{ width: '100%' }}>
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    className={`${classes.headerCell} ${classes.stickyHeader}`}
                                >
                                    {headCell.sortable ? (
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={
                                                orderBy === headCell.id
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={() =>
                                                handleRequestSort(
                                                    headCell.id as
                                                        | keyof User
                                                        | 'activity'
                                                        | 'count'
                                                        | 'initial_training_progress',
                                                )
                                            }
                                        >
                                            {headCell.label}
                                        </TableSortLabel>
                                    ) : (
                                        headCell.label
                                    )}
                                </TableCell>
                            ))}
                            <TableCell
                                className={`${classes.headerCell} ${classes.stickyHeader}`}
                            ></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageData.map((user) => {
                            const activity = userListActivityMap[user.id]
                            return (
                                <TableRow key={user.email}>
                                    <TableCell className={classes.tableCell}>
                                        {(user.profile_picture && (
                                            <AvatarCircle
                                                image={user.profile_picture}
                                                styles={{
                                                    container: {
                                                        minHeight: 50,
                                                        minWidth: 50,
                                                        maxHeight: 50,
                                                        maxWidth: 50,
                                                    },
                                                }}
                                            />
                                        )) || (
                                            <AvatarCircle
                                                text={user.name}
                                                initials={true}
                                                styles={{
                                                    container: {
                                                        minHeight: 50,
                                                        minWidth: 50,
                                                        maxHeight: 50,
                                                        maxWidth: 50,
                                                    },
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <div
                                            style={{
                                                flex: 1,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    flex: 1,
                                                    width: '100%',
                                                }}
                                            >
                                                {user.name}
                                            </div>
                                            {(user.regional_manager ||
                                                user.property_manager) && (
                                                <div
                                                    className={
                                                        classes.roleChips
                                                    }
                                                >
                                                    {user.regional_manager && (
                                                        <Chip
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            label="Regional Manager"
                                                            className={
                                                                classes.regionalManagerChip
                                                            }
                                                        />
                                                    )}
                                                    {user.property_manager && (
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label="Property Manager"
                                                            className={
                                                                classes.propertyManagerChip
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        {user.email}
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        {formatPhoneForDisplay(user.phone)}
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        {formatPhoneForDisplay(user.cell_phone)}
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <div className={classes.activityCell}>
                                            {activity?.latest_timestamp
                                                ? new Date(
                                                      activity.latest_timestamp,
                                                  ).toLocaleString()
                                                : 'N/A'}
                                        </div>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <div className={classes.activityCell}>
                                            {activity?.log_count ?? 'N/A'}
                                        </div>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Tooltip
                                            title={
                                                <span
                                                    style={{ fontSize: '1rem' }}
                                                >
                                                    {user.companies?.length
                                                        ? user.companies
                                                              .map(
                                                                  (company) =>
                                                                      company.name,
                                                              )
                                                              .join(', ')
                                                        : 'No properties'}
                                                </span>
                                            }
                                        >
                                            <span>
                                                {user.companies?.length ?? 0}
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        {(() => {
                                            const progress =
                                                user.initial_training_progress
                                            if (!progress) return '0/4'

                                            const watchedCount = [
                                                progress.video_1_watched,
                                                progress.video_2_watched,
                                                progress.video_3_watched,
                                                progress.video_4_watched,
                                            ].filter(Boolean).length

                                            return `${watchedCount}/4`
                                        })()}
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <div className={classes.actionButtons}>
                                            <Tooltip title="Add to Workspaces">
                                                <IconButton
                                                    color="primary"
                                                    className={
                                                        classes.iconButton
                                                    }
                                                    onClick={() =>
                                                        onClickAddUser(user)
                                                    }
                                                >
                                                    <AddIcon />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Remove from Workspaces">
                                                <IconButton
                                                    color="secondary"
                                                    className={
                                                        classes.iconButton
                                                    }
                                                    onClick={() =>
                                                        onClickRemoveUserFromWorkspaces(
                                                            user,
                                                        )
                                                    }
                                                >
                                                    <RemoveIcon />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="More options">
                                                <IconButton
                                                    className={
                                                        classes.iconButton
                                                    }
                                                    onClick={(e) =>
                                                        handleMoreVertClick(
                                                            e,
                                                            String(user.id),
                                                        )
                                                    }
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="View Activity History">
                                                <IconButton
                                                    className={
                                                        classes.iconButton
                                                    }
                                                    onClick={() =>
                                                        onClickUserActivity(
                                                            user,
                                                        )
                                                    }
                                                    disabled={!activity}
                                                    style={{
                                                        visibility: activity
                                                            ? 'visible'
                                                            : 'hidden',
                                                    }}
                                                >
                                                    <TimelineIcon />
                                                </IconButton>
                                            </Tooltip>

                                            <Popover
                                                open={Boolean(
                                                    anchorEl[user.id],
                                                )}
                                                anchorEl={anchorEl[user.id]}
                                                onClose={() =>
                                                    handlePopoverClose(
                                                        String(user.id),
                                                    )
                                                }
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'right',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'right',
                                                }}
                                                classes={{
                                                    paper: classes.popoverPaper,
                                                }}
                                            >
                                                {propertyManagerSelectionUserId ===
                                                String(user.id) ? (
                                                    <>
                                                        <MenuItem
                                                            className={
                                                                classes.propertySelectHeader
                                                            }
                                                        >
                                                            Select Property
                                                        </MenuItem>
                                                        <MenuItem
                                                            className={
                                                                classes.menuItem
                                                            }
                                                            onClick={() =>
                                                                setPropertyManagerSelectionUserId(
                                                                    null,
                                                                )
                                                            }
                                                        >
                                                            Back
                                                        </MenuItem>
                                                        {user.companies.map(
                                                            (company) => (
                                                                <MenuItem
                                                                    className={
                                                                        classes.menuItem
                                                                    }
                                                                    key={`${user.id}-${company.id}`}
                                                                    onClick={() =>
                                                                        handleMarkAsPropertyManager(
                                                                            user,
                                                                            company.id,
                                                                        )
                                                                    }
                                                                >
                                                                    <ApartmentIcon
                                                                        fontSize="small"
                                                                        className={
                                                                            classes.menuItemIcon
                                                                        }
                                                                    />
                                                                    {
                                                                        company.name
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <MenuItem
                                                            className={
                                                                classes.menuItem
                                                            }
                                                            onClick={() =>
                                                                handleMarkAsRegionalManager(
                                                                    user,
                                                                )
                                                            }
                                                        >
                                                            <PublicIcon
                                                                fontSize="small"
                                                                className={
                                                                    classes.menuItemIcon
                                                                }
                                                            />
                                                            Mark User as
                                                            Regional Manager
                                                        </MenuItem>
                                                        <MenuItem
                                                            className={
                                                                classes.menuItem
                                                            }
                                                            onClick={() =>
                                                                handlePropertyManagerMenuItemClick(
                                                                    user,
                                                                )
                                                            }
                                                            disabled={
                                                                !user.companies
                                                                    ?.length
                                                            }
                                                        >
                                                            <ApartmentIcon
                                                                fontSize="small"
                                                                className={
                                                                    classes.menuItemIcon
                                                                }
                                                            />
                                                            Mark User as
                                                            Property Manager
                                                        </MenuItem>
                                                    </>
                                                )}
                                            </Popover>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Container className={classes.paginationContainer}>
                <div style={{ flex: 1 }} />
                <Pagination
                    count={numPages}
                    page={page}
                    onChange={(_: unknown, newPage: number) => {
                        setPage(newPage)
                    }}
                />
                <div style={{ flex: 1, textAlign: 'right' }}>
                    {start + 1} - {end} of {sortedUsers.length}
                </div>
            </Container>
        </Container>
    )
}
